import React, { useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import CONFIG from "../config";
import BRANDING from "../branding";
import SEO from "../components/common/SEO";

const Signup = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isArabic = i18n.language?.startsWith('ar');

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    securityQuestion: "",
    securityAnswer: "",
    bot_field: "", // Honeypot field
    marketingConsent: false,
    termsConsent: false,
  });
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const currentAppName = isArabic ? BRANDING.appName.ar : BRANDING.appName.en;
  const appInitials = currentAppName
    .trim()
    .split(/[\s-]+/)
    .map(word => word[0])
    .join('')
    .substring(0, 2)
    .toUpperCase() || 'F-S';

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    if (!formData.termsConsent) {
      setMessage(`❌ ${t('auth.consent_required', 'You must agree to the Terms and Privacy Policy')}`);
      setLoading(false);
      return;
    }
    try {
      const res = await axios.post(`${CONFIG.API_URL}/api/auth/signup`, formData);
      setMessage(`✅ ${res.data.message || t('auth.success_signup')}`);
      setTimeout(() => navigate('/login'), 2000);
    } catch (error) {
      setMessage(`❌ ${error.response?.data?.message || t('auth.failed_signup')}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#fcfcfc]">
      <Helmet>
        <html lang={isArabic ? "ar" : "en"} dir={isArabic ? "rtl" : "ltr"} />
        <title>
          {isArabic
            ? `إنشاء حساب | ${BRANDING.appName.ar}`
            : `Sign Up | ${BRANDING.appName.en}`}
        </title>
      </Helmet>
      <SEO />

      <div
        className="w-full max-w-5xl overflow-hidden flex flex-col md:flex-row shadow-2xl"
        style={{ backgroundColor: 'var(--bg-header)', borderRadius: 'var(--card-radius)', border: '1px solid var(--border)' }}
      >
        {/* Left Side - Visual/Branding */}
        <div className="md:w-1/2 bg-gray-900 relative p-12 flex flex-col justify-between text-white overflow-hidden">
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-brand-primary/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-brand-primary/5 rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl"></div>

          <div className="relative z-10">
            <Link to="/" className="text-2xl font-black tracking-tighter hover:text-brand-primary transition-colors flex items-center gap-2">
              <span className="w-8 h-8 bg-brand-primary rounded-lg flex items-center justify-center text-white text-lg">{appInitials}</span>
              {isArabic ? BRANDING.appName.ar : BRANDING.appName.en}
            </Link>
          </div>

          <div className="relative z-10 mb-12">
            <h1 className="text-4xl md:text-5xl font-black mb-6 leading-tight">
              {t('auth.join_us')}
            </h1>
            <p className="text-gray-400 text-lg leading-relaxed max-w-sm">
              {isArabic
                ? "انضم إلى نخبة عملائنا واستمتع بتجربة تسوق فريدة لأرقى أنواع الأثاث المصنوع يدوياً."
                : "Join our elite community and experience the finest hand-crafted furniture for your modern home."
              }
            </p>
          </div>

          <div className="relative z-10 flex items-center gap-4 text-sm text-gray-400">
            <div className="flex -space-x-2">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="w-8 h-8 rounded-full border-2 border-gray-900 bg-gray-800 flex items-center justify-center overflow-hidden">
                  <img src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="user" />
                </div>
              ))}
            </div>
            <span>
              {isArabic ? "أكثر من 2000 عميل يثقون بنا" : "Trusted by 2,000+ elegant homes"}
            </span>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="md:w-1/2 p-8 md:p-16 flex flex-col justify-center">
          <div className="max-w-md mx-auto w-full">
            <div className="mb-10 text-center md:text-left">
              <h2 className="text-3xl font-black mb-2" style={{ color: 'var(--text-header)' }}>{t('auth.signup')}</h2>
              <p className="text-gray-400 font-medium">{t('auth.signup_subtitle', 'Join our elite circle of furniture enthusiasts')}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-bold uppercase tracking-widest px-1" style={{ color: 'var(--text-header)' }}>
                  {t('auth.username')}
                </label>
                <div className="relative">
                  <i className={`fas fa-user absolute ${isArabic ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 text-gray-400`}></i>
                  <input
                    type="text"
                    required
                    style={{ backgroundColor: 'var(--bg-body)', border: '1px solid var(--border)', borderRadius: 'var(--input-radius)' }}
                    className={`w-full ${isArabic ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-4 outline-none transition-all font-medium`}
                    placeholder={t('auth.full_name_placeholder', 'Full Name')}
                    value={formData.username} // Assuming username is now fullName
                    onChange={handleChange} // Keep original handleChange for consistency
                    name="username" // Keep original name for consistency
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-bold uppercase tracking-widest px-1" style={{ color: 'var(--text-header)' }}>
                  {t('auth.email')}
                </label>
                <div className="relative">
                  <i className={`fas fa-envelope absolute ${isArabic ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 text-gray-400`}></i>
                  <input
                    type="email"
                    name="email"
                    placeholder={t('auth.email')}
                    onChange={handleChange}
                    className={`w-full ${isArabic ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-4 outline-none transition-all font-medium`}
                    style={{ backgroundColor: 'var(--bg-body)', border: '1px solid var(--border)', borderRadius: 'var(--input-radius)' }}
                    value={formData.email}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-bold uppercase tracking-widest px-1" style={{ color: 'var(--text-header)' }}>
                  {t('auth.password')}
                </label>
                <div className="relative">
                  <i className={`fas fa-lock absolute ${isArabic ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 text-gray-400`}></i>
                  <input
                    type="password"
                    name="password"
                    placeholder={t('auth.password')}
                    onChange={handleChange}
                    className={`w-full ${isArabic ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-4 outline-none transition-all font-medium`}
                    style={{ backgroundColor: 'var(--bg-body)', border: '1px solid var(--border)', borderRadius: 'var(--input-radius)' }}
                    value={formData.password}
                    required
                  />
                </div>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest px-1">
                  {t('auth.password_hint')}
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-bold uppercase tracking-widest px-1" style={{ color: 'var(--text-header)' }}>
                  {t('auth.security_question', 'Security Question (For Password Reset)')}
                </label>
                <div className="relative">
                  <i className={`fas fa-question-circle absolute ${isArabic ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 text-gray-400`}></i>
                  <select
                    name="securityQuestion"
                    onChange={handleChange}
                    className={`w-full ${isArabic ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-4 outline-none transition-all font-medium appearance-none`}
                    style={{ backgroundColor: 'var(--bg-body)', border: '1px solid var(--border)', borderRadius: 'var(--input-radius)', color: formData.securityQuestion ? 'inherit' : 'gray' }}
                    value={formData.securityQuestion}
                    required
                  >
                    <option value="" disabled>{t('auth.select_question', 'Select a security question')}</option>
                    <option value="What was the name of your first pet?">{t('auth.sq_pet', 'What was the name of your first pet?')}</option>
                    <option value="What is your mother's maiden name?">{t('auth.sq_mother', "What is your mother's maiden name?")}</option>
                    <option value="What city were you born in?">{t('auth.sq_city', 'What city were you born in?')}</option>
                    <option value="What was your childhood nickname?">{t('auth.sq_nickname', 'What was your childhood nickname?')}</option>
                  </select>
                  <i className={`fas fa-chevron-down absolute ${isArabic ? 'left-4' : 'right-4'} top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none`}></i>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-bold uppercase tracking-widest px-1" style={{ color: 'var(--text-header)' }}>
                  {t('auth.security_answer', 'Answer')}
                </label>
                <div className="relative">
                  <i className={`fas fa-key absolute ${isArabic ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 text-gray-400`}></i>
                  <input
                    type="text"
                    name="securityAnswer"
                    placeholder={t('auth.security_answer_placeholder', 'Your secret answer')}
                    onChange={handleChange}
                    className={`w-full ${isArabic ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-4 outline-none transition-all font-medium`}
                    style={{ backgroundColor: 'var(--bg-body)', border: '1px solid var(--border)', borderRadius: 'var(--input-radius)' }}
                    value={formData.securityAnswer}
                    required
                  />
                </div>
              </div>

              {/* International Compliance: Granular explicit consent checkboxes */}
              <div className="space-y-4 pt-2">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="termsConsent"
                    name="termsConsent"
                    checked={formData.termsConsent}
                    onChange={(e) => setFormData({ ...formData, termsConsent: e.target.checked })}
                    className="mt-1 w-4 h-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary cursor-pointer"
                    required
                  />
                  <label htmlFor="termsConsent" className="text-xs text-gray-500 leading-tight cursor-pointer">
                    {t('auth.i_agree_terms', 'I agree to the')} <Link to="/terms" className="text-brand-primary hover:underline">{t('footer.terms', 'Terms & Conditions')}</Link> {t('auth.and', 'and')} <Link to="/privacy" className="text-brand-primary hover:underline">{t('footer.privacy_policy', 'Privacy Policy')}</Link>. *
                  </label>
                </div>

                {BRANDING.compliance?.enableExplicitDataProcessing !== false && (
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="processingConsent"
                      name="processingConsent"
                      checked={formData.processingConsent || false}
                      onChange={(e) => setFormData({ ...formData, processingConsent: e.target.checked })}
                      className="mt-1 w-4 h-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary cursor-pointer"
                      required
                    />
                    <label htmlFor="processingConsent" className="text-xs text-gray-500 leading-tight cursor-pointer">
                      {t('auth.i_consent_processing', 'I explicitly consent to the processing of my personal data according to the')} <a href="/DPA_GLOBAL_TEMPLATE.html" target="_blank" className="text-brand-primary hover:underline">{t('auth.dpa_link', 'Data Processing Agreement')}</a>. *
                    </label>
                  </div>
                )}

                {BRANDING.compliance?.enableEgyptMarketingConsent !== false && (
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="marketingConsent"
                      name="marketingConsent"
                      checked={formData.marketingConsent}
                      onChange={(e) => setFormData({ ...formData, marketingConsent: e.target.checked })}
                      className="mt-1 w-4 h-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary cursor-pointer"
                    />
                    <label htmlFor="marketingConsent" className="text-xs text-gray-500 leading-tight cursor-pointer">
                      {t('auth.marketing_opt_in', 'I would like to receive news, offers, and marketing updates via email/WhatsApp.')}
                    </label>
                  </div>
                )}
              </div>

              {/* Honeypot field - visually hidden to catch bots */}
              <div style={{ display: 'none' }} aria-hidden="true">
                <label htmlFor="bot_field">Leave this field empty</label>
                <input
                  type="text"
                  name="bot_field"
                  id="bot_field"
                  value={formData.bot_field}
                  onChange={handleChange}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gray-900 text-white py-5 rounded-2xl font-black uppercase tracking-widest hover:bg-brand-primary hover:shadow-xl hover:shadow-brand-primary/30 active:scale-[0.98] transition-all disabled:bg-gray-400 disabled:cursor-not-allowed transform"
              >
                {loading ? t('auth.signing_up') : t('auth.signup')}
              </button>
            </form>

            {message && (
              <div className={`mt-6 p-4 rounded-2xl text-center text-sm font-bold ${message.startsWith('✅') ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'}`}>
                {message}
              </div>
            )}

            <div className="mt-10 text-center">
              <p className="text-gray-500 font-medium">
                {t('auth.already_have_account')}{" "}
                <Link to="/login" className="font-black hover:text-brand-primary transition-colors border-b-2 hover:border-brand-primary" style={{ color: 'var(--text-header)', borderColor: 'var(--text-header)' }}>
                  {t('auth.login')}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
